import type { AddQuickMealInput, EditQuickMealInput } from '@front-activerse/common-components';
import {
  calculateCaloriesFromMacros,
  CALORIE_MISMATCH_THRESHOLD,
  parseDecimal,
} from '../AddUserProduct/helpers';

/**
 * A quick meal as the forms see it: nutrition values are PER PORTION, which is what the backend
 * takes on input. The API returns totals (`stored = perPortion × portions`), so
 * `normalizeQuickMeal` divides them back.
 */
export interface QuickMealData {
  id: number;
  name: string;
  /** kcal per portion */
  calories: number;
  /** grams per portion */
  proteinGrams: number;
  fatGrams: number;
  carbsGrams: number;
  fiberGrams: number;
  portions: number;
  imageUrl: string;
  loggedAt?: string;
  notes: string;
  isUserMeal: boolean;
  isFromBaseMeal: boolean;
}

/** Raw shape returned by `dailyTracker.meal` / tracker `events` for a quick meal. */
export interface QuickMealApiData {
  id: number;
  name: string;
  calories: number;
  proteinGrams: number;
  carbsGrams: number;
  fatGrams: number;
  fiberGrams?: number | null;
  portions?: number | null;
  imageUrl?: string | null;
  loggedAt?: string | null;
  notes?: string | null;
  isUserMeal?: boolean | null;
  isFromBaseMeal?: boolean | null;
  isQuickMeal?: boolean | null;
}

export interface QuickMealFormValues {
  date?: string;
  time?: string;
  servings: number;
  saveYourMeal: boolean;
  mealName: string;
  calories: string;
  protein: string;
  fat: string;
  carbs: string;
  fiber: string;
  notes: string;
}

const round = (value: number, decimals: number): number => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/** Divides a stored total back into a per-portion value; a non-positive portion count means "as is". */
export const perPortion = (total: number, portions: number, decimals = 1): number => {
  const safePortions = Number.isFinite(portions) && portions > 0 ? portions : 1;
  return round((total ?? 0) / safePortions, decimals);
};

export const normalizeQuickMeal = (meal: QuickMealApiData): QuickMealData => {
  const portions =
    Number.isFinite(meal.portions) && (meal.portions as number) > 0 ? (meal.portions as number) : 1;
  return {
    id: meal.id,
    name: meal.name ?? '',
    calories: perPortion(meal.calories, portions, 0),
    proteinGrams: perPortion(meal.proteinGrams, portions),
    fatGrams: perPortion(meal.fatGrams, portions),
    carbsGrams: perPortion(meal.carbsGrams, portions),
    fiberGrams: perPortion(meal.fiberGrams ?? 0, portions),
    portions,
    imageUrl: meal.imageUrl ?? '',
    loggedAt: meal.loggedAt ?? undefined,
    notes: meal.notes ?? '',
    isUserMeal: Boolean(meal.isUserMeal),
    isFromBaseMeal: Boolean(meal.isFromBaseMeal),
  };
};

/** An empty quick meal for the "add" screens. */
export const emptyQuickMeal = (imageUrl = ''): QuickMealData => ({
  id: 0,
  name: '',
  calories: 0,
  proteinGrams: 0,
  fatGrams: 0,
  carbsGrams: 0,
  fiberGrams: 0,
  portions: 1,
  imageUrl,
  notes: '',
  isUserMeal: false,
  isFromBaseMeal: false,
});

/** Formats a stored number for an input: no trailing ".0", empty for 0 so placeholders show. */
const toInputString = (value: number): string => {
  if (!Number.isFinite(value) || value === 0) return '';
  return String(round(value, 2));
};

export const quickMealToFormValues = (
  meal: QuickMealData,
  options: { date?: string; time?: string; servings?: number } = {}
): QuickMealFormValues => ({
  ...(options.date !== undefined ? { date: options.date } : {}),
  ...(options.time !== undefined ? { time: options.time } : {}),
  servings: options.servings ?? meal.portions,
  saveYourMeal: false,
  mealName: meal.name,
  calories: toInputString(meal.calories),
  protein: toInputString(meal.proteinGrams),
  fat: toInputString(meal.fatGrams),
  carbs: toInputString(meal.carbsGrams),
  fiber: toInputString(meal.fiberGrams),
  notes: meal.notes,
});

const parseOrZero = (value: string | undefined): number => {
  if (value == null || value.trim() === '') return 0;
  const parsed = parseDecimal(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Nutrition part of the mutation input. Calories are an Int on the API; macros keep two decimals.
 * Fiber is sent explicitly (0 when blank) so clearing the field on edit really clears it.
 */
export const toQuickMealNutritionInput = (
  values: QuickMealFormValues
): Pick<
  AddQuickMealInput,
  'mealName' | 'calories' | 'proteinGrams' | 'carbsGrams' | 'fatGrams' | 'fiberGrams' | 'portions'
> => ({
  mealName: values.mealName.trim(),
  calories: Math.round(parseOrZero(values.calories)),
  proteinGrams: round(parseOrZero(values.protein), 2),
  carbsGrams: round(parseOrZero(values.carbs), 2),
  fatGrams: round(parseOrZero(values.fat), 2),
  fiberGrams: round(parseOrZero(values.fiber), 2),
  portions: Number(values.servings),
});

export const toAddQuickMealInput = (
  values: QuickMealFormValues,
  extra: { dateTime?: string; imageUrl?: string | null; notes?: string | null }
): AddQuickMealInput => ({
  ...toQuickMealNutritionInput(values),
  ...(extra.dateTime ? { dateTime: extra.dateTime } : {}),
  ...(extra.imageUrl ? { imageUrl: extra.imageUrl } : {}),
  ...(extra.notes != null ? { notes: extra.notes } : {}),
});

export const toEditQuickMealInput = (
  values: QuickMealFormValues,
  extra: { mealId: number; dateTime?: string; imageUrl?: string | null; notes?: string | null }
): EditQuickMealInput => ({
  mealId: extra.mealId,
  ...toAddQuickMealInput(values, extra),
});

/**
 * Relative distance between the calories the user typed and the Atwater estimate from the macros
 * (same factors and threshold as the add-product form, so both screens warn on the same input).
 * `null` when it cannot be judged (no / zero calories).
 */
export const quickMealCaloriesMismatchRatio = (values: QuickMealFormValues): number | null => {
  const stated = parseDecimal(values.calories ?? '');
  if (!Number.isFinite(stated) || stated <= 0) return null;
  const calculated = calculateCaloriesFromMacros({
    calories: values.calories,
    protein: values.protein,
    fat: values.fat,
    carbs: values.carbs,
    fiber: values.fiber,
  });
  return Math.abs(stated - calculated) / stated;
};

export const hasQuickMealCaloriesMismatch = (values: QuickMealFormValues): boolean => {
  const ratio = quickMealCaloriesMismatchRatio(values);
  return ratio !== null && ratio > CALORIE_MISMATCH_THRESHOLD;
};

/** True when the user changed anything the backend stores as the meal's own data (not date/portions). */
export const hasNutritionChanged = (meal: QuickMealData, values: QuickMealFormValues): boolean => {
  const input = toQuickMealNutritionInput(values);
  return (
    input.mealName !== meal.name.trim() ||
    input.calories !== Math.round(meal.calories) ||
    input.proteinGrams !== round(meal.proteinGrams, 2) ||
    input.fatGrams !== round(meal.fatGrams, 2) ||
    input.carbsGrams !== round(meal.carbsGrams, 2) ||
    input.fiberGrams !== round(meal.fiberGrams, 2)
  );
};
